import clsx from 'clsx';
import React, { useEffect, useRef, useState } from 'react';
import PropTypes from 'prop-types';
import moment from 'moment';
import {
  TableContainer,
  Table,
  TableHead,
  TableRow,
  TableCell,
  TableBody,
} from '@material-ui/core';
import defaultStyles from './DayWisePlanDataSequenceTable.module.css';
import jlrStyles from './DayWisePlanDataSequenceTableJlr.module.css';
import {
  getCellStyle,
  getSelectedOptionalColumns,
} from '../../utils/tableUtils';
import { isSunday, setStylesAccordingToPlant } from '../../utils/utils';
import { makeStyles } from '@material-ui/core/styles';
import { Popover } from '@material-ui/core';
import { DATE_FORMAT } from '../../constants';
import { usePlantAndLinesStore } from '../../providers/PlantAndLinesStore/PlantAndLinesStore';

const DayWisePlanDataSequenceTable = ({
  planData,
  optionalColumns,
  selectedOptionalCols,
  lastAsnGeneratedDate,
}) => {
  const startDate = moment(planData.startDate, DATE_FORMAT.ISO);
  const endDate = moment(planData.endDate, DATE_FORMAT.ISO);

  const [plant] = usePlantAndLinesStore().plantState;
  const [styles, setStyles] = useState(defaultStyles);
  const isLucknowProlifePlant = plant.label === 'Lucknow-Prolife'

  const getStickyStyle = idx => {
    const styleClasses = [styles.firstCol, styles.secondCol];
    return styleClasses[idx];
  };
  const dates = [];
  while (!startDate.isSame(endDate)) {
    dates.push(moment(startDate));
    startDate.add(1, 'days');
  }

  const leftTable = useRef(null);
  const rightTable = useRef(null);
  const totalRowElem = useRef(null);

  const fixedColumnsStyling = {
    headerStyles: {
      backgroundColor: 'white',
    },
  };
  const fixedColumns = [
    {
      width: 120,
      title: isLucknowProlifePlant?'Part no.':'VC',
      field: 'vc',
      style: {
        textAlign: 'left',
      },
    },
    {
      width: 300,
      title: 'Description',
      field: 'description',
      style: {
        textAlign: 'left',
      },
    },
  ].map(column => ({ ...column, ...fixedColumnsStyling }));

  const optionalColumnsStyles = {
    headerStyles: {
      color: '#707070',
      backgroundColor: 'white',
      zIndex: 1,
    },
  };
  const selectedOptionalColumns = getSelectedOptionalColumns(
    optionalColumns,
    selectedOptionalCols,
    optionalColumnsStyles,
  );

  const dateColumnsStyling = {
    headerStyles: {
      backgroundColor: 'white',
    },
  };
  const dateColumns = dates.map(date => ({
    field: moment(date).format(DATE_FORMAT.ISO),
    title: moment(date).format('DD'),
    disabled: date.isSameOrBefore(lastAsnGeneratedDate),
    isSunday: isSunday(date),
    ...dateColumnsStyling,
  }));

  const columns = [...fixedColumns, ...selectedOptionalColumns];
  const rows = planData.filteredData.map(planData => {
    const { plan, attributes, ...rest } = planData;
    return { ...attributes, ...plan, ...rest };
  });

  const totalRow = {};
  dateColumns.forEach(dateCol => (totalRow[dateCol.field] = 0));
  planData.filteredData.forEach(row => {
    dateColumns.forEach(dateCol => {
      if (row.plan[dateCol.field]) {
        totalRow[dateCol.field] += row.plan[dateCol.field].quantity;
      }
    });
  });

  const [isPopupOpen, setIsPopupOpen] = useState(false);
  const [popoverVal, setPopoverVal] = useState(null);
  const [anchorEl, setAnchorEl] = useState(null);

  const handlePopoverOpen = (event, val) => {
    if (event.currentTarget.scrollWidth > event.currentTarget.clientWidth) {
      setPopoverVal(val);
      setIsPopupOpen(true);
      setAnchorEl(event.currentTarget);
    }
  };

  const handlePopoverClose = event => {
    setIsPopupOpen(false);
    setAnchorEl(null);
  };

  const closePopup = () => setIsPopupOpen(false);
  const usePopoverStyles = makeStyles(theme => ({
    popover: {
      pointerEvents: 'none',
    },
    paper: {
      padding: theme.spacing(1),
      color: 'white',
      backgroundColor: 'grey',
      fontSize: '11.8px', //TODO: keep same as table cell
    },
  }));

  const popoverClasses = usePopoverStyles();

  useEffect(() => {
    if (plant.label)
      setStylesAccordingToPlant(plant, setStyles, defaultStyles, jlrStyles);
  }, [plant]);

  return (
    <div className={styles.sequenceScroll}>
      <TableContainer
        className={styles.attributesTable}
        onScroll={e => {
          rightTable.current.scrollTop = e.target.scrollTop;
        }}
        ref={leftTable}
      >
        <Table stickyHeader className={styles.table}>
          <TableHead className={styles.tableHead}>
            <TableRow>
              {columns.map((col, idx) => (
                <TableCell
                  className={clsx(
                    idx < fixedColumns.length && styles.stickyCol,
                    idx < fixedColumns.length && getStickyStyle(idx),
                    styles.mightOverflow,
                    styles.mTablecellTitle,
                  )}
                  style={{
                    width: col.width,
                    minWidth: col.width,
                    maxWidth: col.width,
                    ...col.headerStyles,
                  }}
                  key={idx}
                >
                  {col.title}
                </TableCell>
              ))}
            </TableRow>
          </TableHead>
          <TableBody>
            {rows.map(row => (
              <TableRow>
                {columns.map((col, idx) => (
                  <TableCell
                    className={clsx(
                      idx < fixedColumns.length && styles.stickyCol,
                      idx < fixedColumns.length && getStickyStyle(idx),
                      styles.mightOverflow,
                      styles.mTablecell,
                    )}
                    onMouseEnter={e => handlePopoverOpen(e, row[col.field])}
                    onMouseLeave={e => handlePopoverClose(e)}
                    style={{
                      width: col.width,
                      minWidth: col.width,
                      maxWidth: col.width,
                      ...col.columnStyles,
                    }}
                  >
                    {row[col.field]}
                  </TableCell>
                ))}
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>
      <TableContainer
        className={styles.datesTable}
        ref={rightTable}
        onScroll={e => {
          leftTable.current.scrollTop = e.target.scrollTop;
          totalRowElem.current.scrollLeft = e.target.scrollLeft;
        }}
      >
        <Table stickyHeader className={styles.table}>
          <TableHead className={styles.tableHead}>
            <TableRow>
              {dateColumns.map(col => (
                <TableCell
                  className={clsx(
                    col.disabled ? styles.disabledCell : styles.mTablecellTitle,
                    col.isSunday && styles.sundayCell,
                  )}
                  style={{ ...col.headerStyles }}
                  key={col.title}
                >
                  {col.title}
                </TableCell>
              ))}
            </TableRow>
          </TableHead>
          <TableBody>
            {rows.map(row => (
              <TableRow>
                {dateColumns.map(date => {
                  const dateString = date.field;
                  return (
                    <TableCell
                      className={getCellStyle(
                        date.disabled,
                        row[dateString] && row[dateString].edited,
                        date.isSunday,
                        styles,
                      )}
                    >
                      {row[dateString] && row[dateString].quantity}
                    </TableCell>
                  );
                })}
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>
      <div className={styles.summaryContainer}>
        <div className={clsx(styles.summaryRow, styles.leftSummaryPart)}>
          <TableCell className={styles.summaryCell} align="right">
            Total (Day wise)
          </TableCell>
        </div>
        <div
          className={clsx(styles.summaryRow, styles.rightSummaryPart)}
          ref={totalRowElem}
          onScroll={e => (rightTable.current.scrollLeft = e.target.scrollLeft)}
        >
          {dateColumns.map(dateCol => (
            <TableCell
              className={
                dateCol.disabled
                  ? clsx(styles.summaryCell, styles.disabledCell)
                  : styles.summaryCell
              }
              style={{ paddingRight: '0.5em' }}
            >
              {totalRow[dateCol.field] || null}
            </TableCell>
          ))}
        </div>
      </div>
      <Popover
        open={isPopupOpen}
        anchorEl={anchorEl}
        onClose={closePopup}
        className={popoverClasses.popover}
        classes={{
          paper: popoverClasses.paper,
        }}
        anchorOrigin={{
          vertical: 'bottom',
          horizontal: 'left',
        }}
        transformOrigin={{
          vertical: 'top',
          horizontal: 'left',
        }}
      >
        {popoverVal}
      </Popover>
    </div>
  );
};

DayWisePlanDataSequenceTable.propTypes = {
  planData: PropTypes.object.isRequired,
  optionalColumns: PropTypes.array,
  selectedOptionalCols: PropTypes.array,
  lastAsnGeneratedDate: PropTypes.objectOf(moment),
};

export default DayWisePlanDataSequenceTable;
